import { useState } from "react";
import { Button, message } from "antd";
import { SwapOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useAcceptHandoverRequestMutation, useRejectHandoverRequestMutation } from "../../services/handover";
import type { HandoverEventData } from "../../hooks/useHandoverSocket";
import "./styles.scss";

interface HandoverRequestPopupProps {
  data:     HandoverEventData | null;
  onClose:  () => void;
}

const HandoverRequestPopup = ({ data, onClose }: HandoverRequestPopupProps) => {
  const [acceptHandoverRequest, { isLoading: accepting }] = useAcceptHandoverRequestMutation();
  const [rejectHandoverRequest, { isLoading: rejecting }] = useRejectHandoverRequestMutation();
  const [action, setAction] = useState<'accept' | 'reject' | null>(null);

  if (!data) return null;

  const handleAccept = async () => {
    if (!data.requestId) return onClose();
    setAction('accept');
    try {
      await acceptHandoverRequest(data.requestId).unwrap();
      message.success('Handover request accepted');
      onClose();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setAction(null);
    }
  };

  const handleReject = async () => {
    if (!data.requestId) return onClose();
    setAction('reject');
    try {
      await rejectHandoverRequest(data.requestId).unwrap();
      message.success('Handover request rejected');
      onClose();
    } catch {
      /* error toast handled globally by badRequestHandler */
    } finally {
      setAction(null);
    }
  };

  return (
    <div className="handover-popup-backdrop">
      <div className="handover-popup-card">
        <div className="handover-popup-icon"><SwapOutlined /></div>
        <h3>New Handover Request</h3>
        {data.requestedByName && <p className="handover-popup-from">From <strong>{data.requestedByName}</strong></p>}
        {data.amount !== undefined && <p className="handover-popup-amount">₹{data.amount.toLocaleString('en-IN')}</p>}
        <p className="handover-popup-message">{data.message || 'A new handover request was raised.'}</p>

        <div className="handover-popup-actions">
          <Button
            type="primary"
            icon={<CheckOutlined />}
            loading={action === 'accept' && accepting}
            disabled={action === 'reject' && rejecting}
            onClick={handleAccept}
          >
            Accept
          </Button>
          <Button
            danger
            icon={<CloseOutlined />}
            loading={action === 'reject' && rejecting}
            disabled={action === 'accept' && accepting}
            onClick={handleReject}
          >
            Reject
          </Button>
        </div>
      </div>
    </div>
  );
};

export default HandoverRequestPopup;
